import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TAG_TYPES,
  getTags,
  createTag,
  updateTag,
  deleteTag,
} from "../../utilities/tags-service";

// One section per tag type. Names keep the casing as typed; the backend
// dedupes them case-insensitively.
const TAG_SECTIONS = [
  { type: "theme", title: "Themes" },
  { type: "colour", title: "Colours" },
  { type: "category", title: "Categories" },
  { type: "series", title: "Series" },
  { type: "designer", title: "Designers" },
  { type: "printingProcess", title: "Printing processes" },
];

function TagSection({ type, title }) {
  const { nameField, label } = TAG_TYPES[type];
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    async function fetchTags() {
      const data = await getTags(type);
      setTags(Array.isArray(data) ? data : []);
      if (!Array.isArray(data)) setError(`Failed to load ${title.toLowerCase()}`);
      setLoading(false);
    }
    fetchTags();
  }, [type, title]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setError("");
    // The service returns the Error on failure
    const created = await createTag(type, newName);
    if (created?._id) {
      setTags([...tags, created]);
      setNewName("");
    } else if (created?.status === 409) {
      setError(`That ${label.toLowerCase()} already exists`);
    } else {
      setError(`Failed to create ${label.toLowerCase()}`);
    }
  }

  async function handleUpdate(id) {
    if (!editingName.trim()) return;
    setError("");
    const updated = await updateTag(type, id, editingName);
    if (updated?._id) {
      setTags(tags.map((t) => (t._id === id ? updated : t)));
      setEditingId(null);
    } else if (updated?.status === 409) {
      setError(`That ${label.toLowerCase()} already exists`);
    } else {
      setError(`Failed to update ${label.toLowerCase()}`);
    }
  }

  async function handleDelete(id) {
    setError("");
    const deleted = await deleteTag(type, id);
    if (deleted instanceof Error) {
      setError(`Failed to delete ${label.toLowerCase()}`);
      return;
    }
    setTags(tags.filter((t) => t._id !== id));
    setConfirmDeleteId(null);
  }

  return (
    <section>
      <h2 className="text-xl font-semibold mb-4">{title}</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={`New ${label.toLowerCase()} name`}
          className="border rounded px-3 py-2 flex-1 min-w-0"
        />
        <button
          type="submit"
          className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
        >
          Add
        </button>
      </form>

      <ul className="space-y-2">
        {loading && <li className="text-gray-400 italic">Loading...</li>}
        {!loading &&
          tags.map((tag) => (
            <li
              key={tag._id}
              className="flex items-center gap-2 border rounded px-3 py-2"
            >
              {editingId === tag._id ? (
                <>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="border rounded px-2 py-1 flex-1 min-w-0"
                    autoFocus
                  />
                  <button
                    onClick={() => handleUpdate(tag._id)}
                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    Cancel
                  </button>
                </>
              ) : confirmDeleteId === tag._id ? (
                <>
                  <span className="flex-1 text-red-600">
                    Delete "{tag[nameField]}"?
                  </span>
                  <button
                    onClick={() => handleDelete(tag._id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1">{tag[nameField]}</span>
                  <button
                    onClick={() => {
                      setEditingId(tag._id);
                      setEditingName(tag[nameField]);
                      setConfirmDeleteId(null);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setConfirmDeleteId(tag._id);
                      setEditingId(null);
                    }}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </>
              )}
            </li>
          ))}
        {!loading && tags.length === 0 && (
          <li className="text-gray-400 italic">
            No {title.toLowerCase()} yet
          </li>
        )}
      </ul>
    </section>
  );
}

export default function AdminPage({ user }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.isAdmin) navigate("/");
  }, [user, navigate]);

  if (!user?.isAdmin) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-bold mb-8">Tag management</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {TAG_SECTIONS.map(({ type, title }) => (
          <TagSection key={type} type={type} title={title} />
        ))}
      </div>
    </div>
  );
}
